/**
 * WeeklyQuizModal
 *
 * Mobile-first weekly tech quiz modal.
 * Features:
 * - Large touch-friendly buttons
 * - Fun animated leaderboard with crown for leader
 * - Smooth transitions between states
 * - Progress tracking with visual feedback
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Brain,
  CheckCircle,
  XCircle,
  ChevronRight,
  Trophy,
  Flame,
  Target,
  Clock,
  Sparkles,
  Users,
  TrendingUp,
  Crown,
  Medal,
  Zap,
  Star,
  Wrench,
} from 'lucide-react';
import { useWeeklyQuiz, useAllQuizStats } from '@/hooks/useWeeklyQuiz';
import {
  getNextQuizResetDate,
  QUIZ_PAUSED,
  QUIZ_PAUSE_HEADLINE,
  QUIZ_PAUSE_MESSAGE,
  QUIZ_RESUME_LABEL,
} from '@/services/quiz.service';
import { QuizCategory } from '@/types';

interface WeeklyQuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  techId: string;
  techName: string;
}

type QuizState = 'intro' | 'question' | 'feedback' | 'results';
type ModalTab = 'quiz' | 'leaderboard';

const CATEGORY_LABELS: Record<QuizCategory, string> = {
  unifi: 'UniFi',
  networking: 'Networking',
  wifi: 'WiFi',
  starlink: 'Starlink',
  security: 'Security',
  smart_home: 'Smart Home',
  troubleshooting: 'Troubleshooting',
  general: 'General',
  company: 'Company',
};

const CATEGORY_COLORS: Record<QuizCategory, string> = {
  unifi: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  networking: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  wifi: 'bg-green-500/20 text-green-400 border-green-500/30',
  starlink: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  security: 'bg-red-500/20 text-red-400 border-red-500/30',
  smart_home: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  troubleshooting: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  general: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  company: 'bg-brand-secondary/20 text-brand-secondary border-brand-secondary/30',
};

export function WeeklyQuizModal({
  isOpen,
  onClose,
  techId,
  techName,
}: WeeklyQuizModalProps) {
  const {
    quiz,
    loading,
    attempt,
    hasCompletedThisWeek,
    currentQuestionIndex,
    currentQuestion,
    startQuiz,
    answerQuestion,
    nextQuestion,
    completeQuiz,
    stats,
  } = useWeeklyQuiz(techId, techName);

  const [quizState, setQuizState] = useState<QuizState>('intro');
  const [selectedAnswer, setSelectedAnswer] = useState<'a' | 'b' | 'c' | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<ModalTab>('quiz');

  // Track whether the user is actively in a quiz session to prevent
  // Firestore onSnapshot updates from overriding UI state mid-quiz
  const quizSessionActiveRef = useRef(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      quizSessionActiveRef.current = false;
      return;
    }

    // Don't override quiz state during an active session - user actions
    // (handleSelectAnswer, handleNext) control state transitions
    if (quizSessionActiveRef.current) return;

    if (hasCompletedThisWeek) {
      setQuizState('results');
    } else if (attempt && attempt.status === 'in_progress') {
      // Restore the exact screen state where user left off
      const savedScreen = attempt.currentScreen || 'question';
      setQuizState(savedScreen);

      // If resuming on feedback screen, restore the answer state
      if (savedScreen === 'feedback' && attempt.answers.length > 0) {
        const lastAnswer = attempt.answers[attempt.answers.length - 1];
        setSelectedAnswer(lastAnswer.selectedAnswer);
        setIsCorrect(lastAnswer.isCorrect);
      } else {
        setSelectedAnswer(null);
        setIsCorrect(null);
      }
    } else {
      setQuizState('intro');
      setSelectedAnswer(null);
      setIsCorrect(null);
    }
  }, [isOpen, hasCompletedThisWeek, attempt]);

  // Recovery: auto-complete stale attempts where all questions were answered
  // but completeQuiz() was never called (e.g. from previous snapshot-override bug)
  useEffect(() => {
    if (!isOpen || quizSessionActiveRef.current) return;
    if (!quiz || !attempt) return;
    if (attempt.status === 'in_progress' && attempt.answers.length >= quiz.questions.length) {
      quizSessionActiveRef.current = true;
      completeQuiz()
        .then(() => {
          setQuizState('results');
        })
        .catch((err) => {
          console.error('Failed to auto-complete stale quiz attempt:', err);
        });
    }
  }, [isOpen, quiz, attempt, completeQuiz]);

  const handleStartQuiz = async () => {
    try {
      quizSessionActiveRef.current = true;
      await startQuiz();
      setQuizState('question');
    } catch (error) {
      console.error('Failed to start quiz:', error);
      quizSessionActiveRef.current = false;
    }
  };

  const handleSelectAnswer = async (answer: 'a' | 'b' | 'c') => {
    if (isSubmitting || selectedAnswer !== null) return;

    setIsSubmitting(true);
    setSelectedAnswer(answer);

    try {
      const correct = await answerQuestion(answer);
      setIsCorrect(correct);
      setQuizState('feedback');
    } catch (error) {
      console.error('Failed to submit answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (!quiz) return;

    const isLastQuestion = currentQuestionIndex >= quiz.questions.length - 1;

    if (isLastQuestion) {
      await completeQuiz();
      setQuizState('results');
    } else {
      await nextQuestion(); // Now async - saves UI state
      setSelectedAnswer(null);
      setIsCorrect(null);
      setQuizState('question');
    }
  };

  // Escape key closes the modal — defensive fallback if X button is unreachable
  // (e.g. covered by an animating overlay or scrolled off-screen on a small device).
  // MUST be declared before any conditional return to satisfy the Rules of Hooks —
  // otherwise the hook count changes between renders and React crashes with #310,
  // which manifests as a white screen on iOS Safari.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Quiz on pause — short-circuit to a single calm screen.
  if (QUIZ_PAUSED) {
    return (
      <AnimatePresence>
        <motion.div
          key="quiz-paused-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-surface-overlay backdrop-blur-sm z-[150] flex items-end sm:items-center justify-center"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            key="quiz-paused-card"
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-surface-card border-t sm:border border-theme rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md overflow-hidden shadow-2xl"
          >
            <div className="flex items-center justify-end p-3">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                aria-label="Close"
                title="Close (Esc)"
                className="p-2 hover:bg-surface-glass rounded-xl transition-colors active:scale-95"
              >
                <X className="w-5 h-5 text-slate-400 pointer-events-none" />
              </button>
            </div>
            <div className="px-6 pb-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12 }}
                className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-brand-secondary/30 to-brand-secondary/10 border border-brand-secondary/30 flex items-center justify-center"
              >
                <Wrench className="w-9 h-9 text-brand-secondary" />
              </motion.div>
              <h2 className="text-xl sm:text-2xl font-bold text-theme-primary mb-2">
                {QUIZ_PAUSE_HEADLINE}
              </h2>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed mb-6 max-w-sm mx-auto">
                {QUIZ_PAUSE_MESSAGE}
              </p>
              <div className="bg-surface-glass border border-theme rounded-xl px-4 py-3 mb-6 inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brand-secondary" />
                <span className="text-sm font-semibold text-theme-primary">
                  Back in {QUIZ_RESUME_LABEL}
                </span>
              </div>
              <div>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="w-full py-3 bg-surface-glass hover:bg-surface-raised text-theme-primary font-semibold rounded-xl transition-all"
                >
                  Got it
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        key="quiz-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-surface-overlay backdrop-blur-sm z-[150] flex items-end sm:items-center justify-center"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          key="quiz-card"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-surface-card border-t sm:border border-theme rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl"
        >
          {/* Header - Compact on mobile */}
          <div className="p-4 sm:p-6 border-b border-theme">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-brand-secondary to-brand-secondary rounded-xl shadow-lg">
                  <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-theme-primary">Weekly Quiz</h2>
                  <p className="text-xs text-slate-400">New quiz every Monday</p>
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  // stopPropagation so the click doesn't bubble to the
                  // backdrop's onClick (which only fires on direct backdrop
                  // hits, but stopping here is belt-and-suspenders against
                  // any wrapping element that might intercept).
                  e.stopPropagation();
                  onClose();
                }}
                aria-label="Close quiz"
                title="Close (Esc)"
                className="p-2 hover:bg-surface-glass rounded-xl transition-colors active:scale-95 z-10 relative"
              >
                <X className="w-5 h-5 text-slate-400 pointer-events-none" />
              </button>
            </div>

            {/* Tabs - Full width on mobile */}
            <div className="flex gap-1 bg-surface-glass rounded-xl p-1">
              <button
                onClick={() => setActiveTab('quiz')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all active:scale-95 ${
                  activeTab === 'quiz'
                    ? 'bg-gradient-to-r from-brand-secondary to-brand-secondary text-white shadow-lg'
                    : 'text-slate-400 hover:text-theme-primary hover:bg-surface-glass'
                }`}
              >
                <Brain className="w-4 h-4" />
                Quiz
              </button>
              <button
                onClick={() => setActiveTab('leaderboard')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all active:scale-95 ${
                  activeTab === 'leaderboard'
                    ? 'bg-gradient-to-r from-brand-secondary to-brand-secondary text-white shadow-lg'
                    : 'text-slate-400 hover:text-theme-primary hover:bg-surface-glass'
                }`}
              >
                <Trophy className="w-4 h-4" />
                Leaderboard
              </button>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-180px)]">
            <AnimatePresence mode="wait">
              {activeTab === 'leaderboard' ? (
                <motion.div
                  key="leaderboard"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <LeaderboardScreen currentTechId={techId} />
                </motion.div>
              ) : loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center py-12"
                >
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-secondary" />
                </motion.div>
              ) : quizState === 'intro' ? (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <IntroScreen
                    onStart={handleStartQuiz}
                    stats={stats}
                    questionCount={quiz?.questions.length || 5}
                    onViewLeaderboard={() => setActiveTab('leaderboard')}
                  />
                </motion.div>
              ) : quizState === 'question' && currentQuestion ? (
                <motion.div
                  key={`question-${currentQuestionIndex}`}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                >
                  <QuestionScreen
                    question={currentQuestion}
                    questionIndex={currentQuestionIndex}
                    totalQuestions={quiz?.questions.length || 5}
                    selectedAnswer={selectedAnswer}
                    onSelectAnswer={handleSelectAnswer}
                    isSubmitting={isSubmitting}
                  />
                </motion.div>
              ) : quizState === 'feedback' && currentQuestion ? (
                <motion.div
                  key={`feedback-${currentQuestionIndex}`}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                >
                  <FeedbackScreen
                    question={currentQuestion}
                    selectedAnswer={selectedAnswer!}
                    isCorrect={isCorrect!}
                    onNext={handleNext}
                    isLastQuestion={currentQuestionIndex >= (quiz?.questions.length || 5) - 1}
                  />
                </motion.div>
              ) : quizState === 'results' ? (
                attempt ? (
                  <motion.div
                    key="results"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ResultsScreen
                      attempt={attempt}
                      stats={stats}
                      onClose={onClose}
                      onViewLeaderboard={() => setActiveTab('leaderboard')}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="results-loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center py-12"
                  >
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-secondary mb-4" />
                    <p className="text-slate-400 text-sm">Loading your results...</p>
                  </motion.div>
                )
              ) : null}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================================================
// INTRO SCREEN - Mobile optimized
// ============================================================================

interface IntroScreenProps {
  onStart: () => void;
  stats: ReturnType<typeof useWeeklyQuiz>['stats'];
  questionCount: number;
  onViewLeaderboard: () => void;
}

function IntroScreen({ onStart, stats, questionCount, onViewLeaderboard }: IntroScreenProps) {
  return (
    <div className="text-center py-4">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', damping: 10 }}
        className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-brand-secondary to-brand-secondary rounded-full flex items-center justify-center mx-auto mb-5 shadow-xl shadow-brand-secondary/30"
      >
        <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
      </motion.div>

      <h3 className="text-xl sm:text-2xl font-bold text-theme-primary mb-2">Ready for This Week's Quiz?</h3>
      <p className="text-slate-400 mb-6 text-sm sm:text-base">
        {questionCount} questions • UniFi, networking, WiFi & more
      </p>

      {/* Quick Stats - 2 columns on mobile, 3 on desktop */}
      {stats && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-surface-glass rounded-xl p-3 sm:p-4 border border-theme"
          >
            <div className="flex items-center justify-center gap-1 text-brand-secondary mb-1">
              <Trophy className="w-4 h-4" />
              <span className="text-lg sm:text-xl font-bold">{stats.lifetimeAverageScore}%</span>
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400">Avg Score</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-surface-glass rounded-xl p-3 sm:p-4 border border-theme"
          >
            <div className="flex items-center justify-center gap-1 text-orange-400 mb-1">
              <Flame className="w-4 h-4" />
              <span className="text-lg sm:text-xl font-bold">{stats.currentStreak}</span>
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400">Week Streak</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-surface-glass rounded-xl p-3 sm:p-4 border border-theme"
          >
            <div className="flex items-center justify-center gap-1 text-purple-400 mb-1">
              <Target className="w-4 h-4" />
              <span className="text-lg sm:text-xl font-bold">{stats.totalQuizzesTaken}</span>
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400">Quizzes</div>
          </motion.div>
        </div>
      )}

      {/* Action Buttons - Large for mobile */}
      <div className="flex flex-col gap-3">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={onStart}
          className="w-full py-4 sm:py-5 bg-gradient-to-r from-brand-secondary to-brand-secondary text-white font-bold rounded-xl shadow-lg shadow-brand-secondary/25 text-lg flex items-center justify-center gap-2"
        >
          <Zap className="w-5 h-5" />
          Start Quiz
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={onViewLeaderboard}
          className="w-full py-3 bg-surface-glass hover:bg-surface-raised text-theme-primary font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
        >
          <Trophy className="w-4 h-4" />
          View Leaderboard
        </motion.button>
      </div>
    </div>
  );
}

// ============================================================================
// DETERMINISTIC ANSWER SHUFFLE
// ============================================================================

/**
 * Shuffle answer options using a seeded PRNG derived from the question ID.
 * This ensures:
 * 1. Options are in a different order each question (fixes "always B" bug)
 * 2. QuestionScreen and FeedbackScreen show the same order for the same question
 * 3. The shuffle is stable across re-renders
 *
 * The `key` field preserves the original option key ('a'|'b'|'c') so that
 * correctness is checked by VALUE, not by position.
 */
function shuffleOptions(question: { id: string; options: { a: string; b: string; c: string } }) {
  const raw: { key: 'a' | 'b' | 'c'; text: string }[] = [
    { key: 'a', text: question.options.a },
    { key: 'b', text: question.options.b },
    { key: 'c', text: question.options.c },
  ];
  // Seeded PRNG from question ID — deterministic so both screens match
  let seed = 0;
  for (let i = 0; i < question.id.length; i++) seed += question.id.charCodeAt(i);
  for (let i = raw.length - 1; i > 0; i--) {
    seed = (seed * 9301 + 49297) % 233280;
    const j = seed % (i + 1);
    [raw[i], raw[j]] = [raw[j], raw[i]];
  }
  return raw.map((opt, idx) => ({ ...opt, label: String.fromCharCode(65 + idx) }));
}

// ============================================================================
// QUESTION SCREEN - Large touch targets
// ============================================================================

interface QuestionScreenProps {
  question: NonNullable<ReturnType<typeof useWeeklyQuiz>['currentQuestion']>;
  questionIndex: number;
  totalQuestions: number;
  selectedAnswer: 'a' | 'b' | 'c' | null;
  onSelectAnswer: (answer: 'a' | 'b' | 'c') => void;
  isSubmitting: boolean;
}

function QuestionScreen({
  question,
  questionIndex,
  totalQuestions,
  selectedAnswer,
  onSelectAnswer,
  isSubmitting,
}: QuestionScreenProps) {
  // Shuffle answer options so correct answer isn't always in position B.
  // Uses a deterministic seed from question.id so FeedbackScreen shows
  // the same order.
  const options = useMemo(() => shuffleOptions(question), [question.id]);

  return (
    <div>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">
            Question {questionIndex + 1} of {totalQuestions}
          </span>
          <span
            className={`text-xs px-2 py-1 rounded-full border ${CATEGORY_COLORS[question.category]}`}
          >
            {CATEGORY_LABELS[question.category]}
          </span>
        </div>
        <div className="h-2 bg-surface-glass rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}
            className="h-full bg-gradient-to-r from-brand-secondary to-brand-secondary rounded-full"
          />
        </div>
      </div>

      {/* Question */}
      <h3 className="text-lg sm:text-xl font-semibold text-theme-primary mb-5 leading-relaxed">{question.question}</h3>

      {/* Options - Large touch targets */}
      <div className="space-y-3">
        {options.map((option, index) => (
          <motion.button
            key={option.key}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onSelectAnswer(option.key)}
            disabled={isSubmitting || selectedAnswer !== null}
            className={`w-full p-4 sm:p-5 rounded-xl border text-left transition-all flex items-center gap-4 ${
              selectedAnswer === option.key
                ? 'bg-brand-secondary/20 border-brand-secondary text-white ring-2 ring-brand-secondary'
                : 'bg-surface-glass border-theme text-slate-300 hover:bg-surface-glass hover:border-theme active:bg-surface-raised'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-lg shrink-0 ${
                selectedAnswer === option.key
                  ? 'bg-brand-secondary text-white'
                  : 'bg-surface-glass text-slate-400'
              }`}
            >
              {option.label}
            </span>
            <span className="flex-1 text-sm sm:text-base leading-snug">{option.text}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// FEEDBACK SCREEN - Clear visual feedback
// ============================================================================

interface FeedbackScreenProps {
  question: NonNullable<ReturnType<typeof useWeeklyQuiz>['currentQuestion']>;
  selectedAnswer: 'a' | 'b' | 'c';
  isCorrect: boolean;
  onNext: () => void;
  isLastQuestion: boolean;
}

function FeedbackScreen({
  question,
  selectedAnswer,
  isCorrect,
  onNext,
  isLastQuestion,
}: FeedbackScreenProps) {
  const options = useMemo(() => shuffleOptions(question), [question.id]);

  return (
    <div>
      {/* Feedback Banner */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={`p-4 rounded-xl mb-5 flex items-center gap-3 ${
          isCorrect
            ? 'bg-green-500/20 border-2 border-green-500/50'
            : 'bg-red-500/20 border-2 border-red-500/50'
        }`}
      >
        {isCorrect ? (
          <>
            <motion.div
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring' }}
            >
              <CheckCircle className="w-10 h-10 text-green-400" />
            </motion.div>
            <div>
              <div className="text-lg font-bold text-green-400">Correct! 🎉</div>
              <div className="text-sm text-green-400/80">Great job!</div>
            </div>
          </>
        ) : (
          <>
            <motion.div
              initial={{ rotate: 180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring' }}
            >
              <XCircle className="w-10 h-10 text-red-400" />
            </motion.div>
            <div>
              <div className="text-lg font-bold text-red-400">Not quite 😅</div>
              <div className="text-sm text-red-400/80">
                Answer: <strong>{options.find(o => o.key === question.correctAnswer)?.label || question.correctAnswer.toUpperCase()}</strong>
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* Question */}
      <h3 className="text-base text-slate-300 mb-4">{question.question}</h3>

      {/* Options with correct/incorrect highlighting */}
      <div className="space-y-2 mb-5">
        {options.map((option) => {
          const isSelected = selectedAnswer === option.key;
          const isCorrectAnswer = question.correctAnswer === option.key;

          let bgClass = 'bg-surface-glass border-theme';
          let iconElement = null;

          if (isCorrectAnswer) {
            bgClass = 'bg-green-500/20 border-green-500/50';
            iconElement = <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />;
          } else if (isSelected && !isCorrect) {
            bgClass = 'bg-red-500/20 border-red-500/50';
            iconElement = <XCircle className="w-5 h-5 text-red-400 shrink-0" />;
          }

          return (
            <div
              key={option.key}
              className={`p-3 rounded-xl border flex items-center gap-3 ${bgClass}`}
            >
              <span
                className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold shrink-0 ${
                  isCorrectAnswer
                    ? 'bg-green-500 text-white'
                    : isSelected
                    ? 'bg-red-500 text-white'
                    : 'bg-surface-glass text-slate-400'
                }`}
              >
                {option.label}
              </span>
              <span className="flex-1 text-sm text-slate-300">{option.text}</span>
              {iconElement}
            </div>
          );
        })}
      </div>

      {/* Explanation */}
      <div className="bg-surface-glass rounded-xl p-4 border border-theme mb-5">
        <div className="text-xs text-slate-400 mb-1 flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          Explanation
        </div>
        <div className="text-sm text-theme-primary leading-relaxed">{question.explanation}</div>
      </div>

      {/* Next Button - Large for mobile */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={onNext}
        className="w-full py-4 bg-gradient-to-r from-brand-secondary to-brand-secondary text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 text-lg"
      >
        {isLastQuestion ? 'See Results' : 'Next Question'}
        <ChevronRight className="w-5 h-5" />
      </motion.button>
    </div>
  );
}

// ============================================================================
// RESULTS SCREEN - Celebratory
// ============================================================================

interface ResultsScreenProps {
  attempt: NonNullable<ReturnType<typeof useWeeklyQuiz>['attempt']>;
  stats: ReturnType<typeof useWeeklyQuiz>['stats'];
  onClose: () => void;
  onViewLeaderboard: () => void;
}

function ResultsScreen({ attempt, stats, onClose, onViewLeaderboard }: ResultsScreenProps) {
  const percentage = attempt.score;
  const isGoodScore = percentage >= 80;
  const isOkScore = percentage >= 60;

  return (
    <div className="text-center py-2">
      {/* Score Circle with animation */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', damping: 10 }}
        className="relative w-28 h-28 sm:w-32 sm:h-32 mx-auto mb-5"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
          <circle
            cx="50"
            cy="50"
            r="42"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-theme-muted"
          />
          <motion.circle
            cx="50"
            cy="50"
            r="42"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            initial={{ strokeDasharray: '0 264' }}
            animate={{
              strokeDasharray: `${(percentage / 100) * 263.9} 264`,
            }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className={
              isGoodScore
                ? 'text-green-400'
                : isOkScore
                ? 'text-yellow-400'
                : 'text-red-400'
            }
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-3xl sm:text-4xl font-bold text-theme-primary"
          >
            {percentage}%
          </motion.span>
        </div>
      </motion.div>

      {/* Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h3 className="text-xl sm:text-2xl font-bold text-theme-primary mb-1">
          {isGoodScore
            ? '🏆 Excellent Work!'
            : isOkScore
            ? '👍 Good Effort!'
            : '💪 Keep Practicing!'}
        </h3>
        <p className="text-slate-400 mb-5 text-sm">
          {attempt.correctAnswers} of {attempt.totalQuestions} correct
        </p>
      </motion.div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-surface-glass rounded-xl p-3 border border-theme"
          >
            <div className="flex items-center justify-center gap-1 text-orange-400 mb-1">
              <Flame className="w-4 h-4" />
              <span className="text-lg font-bold">{stats.currentStreak}</span>
            </div>
            <div className="text-[10px] text-slate-400">Streak</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-surface-glass rounded-xl p-3 border border-theme"
          >
            <div className="flex items-center justify-center gap-1 text-brand-secondary mb-1">
              <Trophy className="w-4 h-4" />
              <span className="text-lg font-bold">{stats.lifetimeAverageScore}%</span>
            </div>
            <div className="text-[10px] text-slate-400">Lifetime</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-surface-glass rounded-xl p-3 border border-theme"
          >
            <div className="flex items-center justify-center gap-1 text-purple-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-lg font-bold">
                {attempt.timeSpentSeconds
                  ? Math.floor(attempt.timeSpentSeconds / 60)
                  : 0}m
              </span>
            </div>
            <div className="text-[10px] text-slate-400">Time</div>
          </motion.div>
        </div>
      )}

      {/* Retake lockout message */}
      <RetakeLockoutMessage />

      {/* Action Buttons */}
      <div className="flex flex-col gap-3">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={onViewLeaderboard}
          className="w-full py-4 bg-gradient-to-r from-brand-secondary to-brand-secondary text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2"
        >
          <Crown className="w-5 h-5" />
          View Leaderboard
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={onClose}
          className="w-full py-3 bg-surface-glass hover:bg-surface-raised text-theme-primary font-semibold rounded-xl transition-all"
        >
          Done
        </motion.button>
      </div>
    </div>
  );
}

/**
 * Shows how many days until the quiz resets and can be retaken.
 */
function RetakeLockoutMessage() {
  const { daysUntil } = useMemo(() => getNextQuizResetDate(), []);

  if (daysUntil === 0) return null;

  return (
    <div className="bg-surface-glass rounded-xl p-3 border border-theme mb-5 flex items-center gap-2.5">
      <Clock className="w-4 h-4 text-brand-secondary shrink-0" />
      <p className="text-xs text-slate-400">
        Retake available in <span className="text-brand-secondary font-semibold">{daysUntil} day{daysUntil !== 1 ? 's' : ''}</span> — new quiz drops Monday 6am
      </p>
    </div>
  );
}

// ============================================================================
// LEADERBOARD SCREEN - Fun & Interactive with Crown
// ============================================================================

interface LeaderboardScreenProps {
  currentTechId: string;
}

function LeaderboardScreen({ currentTechId }: LeaderboardScreenProps) {
  const { stats, loading } = useAllQuizStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-secondary" />
      </div>
    );
  }

  if (stats.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-theme-primary mb-2">No Rankings Yet</h3>
        <p className="text-slate-400">Be the first to complete a quiz!</p>
      </div>
    );
  }

  // Calculate fun stats
  const totalQuizzes = stats.reduce((sum: number, s) => sum + s.totalQuizzesTaken, 0);
  const avgScore = Math.round(
    stats.reduce((sum: number, s) => sum + s.lifetimeAverageScore, 0) / stats.length
  );
  const bestStreak = Math.max(...stats.map((s) => s.longestStreak || 0));
  const leader = stats[0];

  return (
    <div>
      {/* Leader Spotlight */}
      {leader && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-yellow-500/20 via-amber-500/10 to-orange-500/20 rounded-2xl p-4 mb-5 border-2 border-yellow-500/30 relative overflow-hidden"
        >
          {/* Sparkle effects */}
          <div className="absolute top-2 right-4 animate-pulse">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          </div>
          <div className="absolute bottom-3 right-8 animate-pulse delay-300">
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
          </div>

          <div className="flex items-center gap-4">
            <div className="relative">
              <motion.div
                animate={{ rotate: [0, -5, 5, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute -top-3 left-1/2 -translate-x-1/2"
              >
                <Crown className="w-8 h-8 text-yellow-400 fill-yellow-400 drop-shadow-lg" />
              </motion.div>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-yellow-500/30 mt-2">
                👑
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-yellow-400 font-medium mb-0.5">QUIZ CHAMPION</div>
              <div className="text-lg font-bold text-theme-primary flex items-center gap-2">
                {leader.techName}
                {leader.techId === currentTechId && (
                  <span className="text-xs bg-cyan-500/30 text-cyan-400 px-2 py-0.5 rounded-full">You!</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                <span className="flex items-center gap-1">
                  <Trophy className="w-3 h-3 text-yellow-400" />
                  {leader.lifetimeAverageScore}% avg
                </span>
                <span className="flex items-center gap-1">
                  <Flame className="w-3 h-3 text-orange-400" />
                  {leader.currentStreak} streak
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-yellow-400">#1</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-surface-glass rounded-xl p-3 border border-theme text-center"
        >
          <div className="flex items-center justify-center gap-1 text-purple-400 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-lg font-bold">{stats.length}</span>
          </div>
          <div className="text-[10px] text-slate-400">Players</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-surface-glass rounded-xl p-3 border border-theme text-center"
        >
          <div className="flex items-center justify-center gap-1 text-green-400 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-lg font-bold">{avgScore}%</span>
          </div>
          <div className="text-[10px] text-slate-400">Team Avg</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-surface-glass rounded-xl p-3 border border-theme text-center"
        >
          <div className="flex items-center justify-center gap-1 text-orange-400 mb-1">
            <Flame className="w-4 h-4" />
            <span className="text-lg font-bold">{bestStreak}</span>
          </div>
          <div className="text-[10px] text-slate-400">Best Streak</div>
        </motion.div>
      </div>

      {/* Rankings */}
      <h4 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
        <Medal className="w-4 h-4" />
        Rankings
      </h4>
      <div className="space-y-2">
        {stats.map((techStats, index) => {
          const isCurrentUser = techStats.techId === currentTechId;
          const isLeader = index === 0;
          const isSecond = index === 1;
          const isThird = index === 2;

          return (
            <motion.div
              key={techStats.techId}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                isLeader
                  ? 'bg-gradient-to-r from-yellow-500/20 to-transparent border-2 border-yellow-500/40'
                  : isSecond
                  ? 'bg-gradient-to-r from-slate-400/20 to-transparent border border-slate-400/30'
                  : isThird
                  ? 'bg-gradient-to-r from-amber-700/20 to-transparent border border-amber-700/30'
                  : isCurrentUser
                  ? 'bg-cyan-500/10 border-2 border-cyan-500/40'
                  : 'bg-surface-glass border border-theme'
              }`}
            >
              {/* Rank Badge */}
              <div className="relative">
                {isLeader && (
                  <Crown className="w-4 h-4 text-yellow-400 fill-yellow-400 absolute -top-2 left-1/2 -translate-x-1/2" />
                )}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
                    isLeader
                      ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-lg shadow-yellow-500/30'
                      : isSecond
                      ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-slate-800'
                      : isThird
                      ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white'
                      : 'bg-surface-glass text-slate-400'
                  }`}
                >
                  {index + 1}
                </div>
              </div>

              {/* Name & Stats */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium truncate ${
                    isLeader ? 'text-yellow-400' :
                    isCurrentUser ? 'text-cyan-400' :
                    'text-theme-primary'
                  }`}>
                    {techStats.techName}
                  </span>
                  {isCurrentUser && !isLeader && (
                    <span className="text-[10px] bg-cyan-500/30 text-cyan-400 px-1.5 py-0.5 rounded">You</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                  <span>{techStats.totalQuizzesTaken} quizzes</span>
                  {techStats.currentStreak > 0 && (
                    <span className="flex items-center gap-0.5 text-orange-400">
                      <Flame className="w-3 h-3" />
                      {techStats.currentStreak}
                    </span>
                  )}
                </div>
              </div>

              {/* Score */}
              <div className="text-right">
                <div
                  className={`text-lg font-bold ${
                    techStats.lifetimeAverageScore >= 80
                      ? 'text-green-400'
                      : techStats.lifetimeAverageScore >= 60
                      ? 'text-yellow-400'
                      : 'text-red-400'
                  }`}
                >
                  {techStats.lifetimeAverageScore}%
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Fun Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-5 text-center"
      >
        <p className="text-xs text-slate-500">
          🎯 Total quizzes completed: <span className="text-brand-secondary font-semibold">{totalQuizzes}</span>
        </p>
      </motion.div>
    </div>
  );
}
